import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { DIAGNOSTIC_MODULE_ROLES, getCurrentRole, readTransactionListState, resolveListBackTarget, SUBSCRIBER_LIST_PATH, SUBSCRIBER_LIST_URL_KEY, writeTransactionListState } from '../utils/subscriberHelpers';
import { useSubscriberData } from '../hooks/useSubscriberData';
import { useTransactionHistory, useTransactionDetail } from '../hooks/useTransactionHistory';
import { MODULE_KIND_DIAGNOSTICS, MODULE_KIND_MEDICINE, useModuleBookingDetail } from '../hooks/useModuleBookingDetail';
import { useRedemptionHistory } from '../hooks/useRedemptionData';
import { useFamilyMembers } from '../hooks/useFamilyMembers';
import SubscriberInfoCard from '../components/subscriber/SubscriberInfoCard';
import WalletSnapshotCard from '../components/subscriber/WalletSnapshotCard';
import TransactionHistoryTable from '../components/subscriber/TransactionHistoryTable';
import TransactionDetailModal from '../components/subscriber/TransactionDetailModal';
import MedicineBookingDetailModal from '../components/medicine/MedicineBookingDetailModal';
import DiagnosticBookingDetailModal from '../components/diagnostic/DiagnosticBookingDetailModal';
import MedicineTransaction from './MedicineTransaction';
import RedemptionHistoryTable from '../components/redemption/RedemptionHistoryTable';
import BankDetailsCard from '../components/redemption/BankDetailsCard';
import FamilyMembersList from '../components/subscriber/FamilyMembersList';
import AddFamilyMemberModal from '../components/subscriber/AddFamilyMemberModal';
import EditFamilyMemberModal from '../components/subscriber/EditFamilyMemberModal';
import DeactivateFamilyMemberModal from '../components/subscriber/DeactivateFamilyMemberModal';
import NotificationToast from '../components/common/NotificationToast';
import { normalizeRelationshipForApi } from '../utils/subscriberHelpers';
import { getSubscriberDocuments, getWalletActivationDetail, getSubscriberCreditSummary } from '../services/subscriberWalletService';
import { getCreditLineSubscriberDetail } from '../services/creditLineService';
// WalletActivationCard hidden from this page (see render site below) — the
// Regenerate PIN tool it contains is unaffected and can be restored by
// uncommenting its render block; the import stays live since a later
// restoration should not need to re-add it.
import WalletActivationCard from '../components/subscriber/WalletActivationCard'; // eslint-disable-line no-unused-vars
// CreditActivationCard hidden from this page per product decision — not
// deleted, only unmounted below. Import kept live for restoration.
import CreditActivationCard from '../components/subscriber/CreditActivationCard'; // eslint-disable-line no-unused-vars
import CreditSnapshotCard from '../components/subscriber/CreditSnapshotCard';

const SubscriberProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentRole = getCurrentRole();
  // The list page/filters this subscriber was opened from, so Back to List lands on the same rows.
  const backToList = resolveListBackTarget(location.state, SUBSCRIBER_LIST_PATH, SUBSCRIBER_LIST_URL_KEY);

  // Coming back from a medicine/diagnostics module page, the tab and page the user left
  // this list on ride in the URL — restore them so back lands on the same view they
  // clicked from. A first visit has no such params and gets the defaults.
  const initialTxnState = useMemo(() => readTransactionListState(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps
  
  const [mounted, setMounted] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showPhone, setShowPhone] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [mobilesUnmasked, setMobilesUnmasked] = useState(false);
  const [partnerDataExpanded, setPartnerDataExpanded] = useState(false);
  
  // Transaction filters
  const [txnSearch, setTxnSearch] = useState('');
  const [txnCategory, setTxnCategory] = useState(initialTxnState.txnCategory);
  const [txnPage, setTxnPage] = useState(initialTxnState.txnPage);
  
  // Redemption filters
  const [redemptionSearch, setRedemptionSearch] = useState('');
  const [redemptionSearchInput, setRedemptionSearchInput] = useState('');
  const [redemptionStatus, setRedemptionStatus] = useState('');
  const [redemptionPage, setRedemptionPage] = useState(1);
  const [redemptionInitialLoadComplete, setRedemptionInitialLoadComplete] = useState(false);
  const redemptionSearchTimeoutRef = useRef(null);
  
  // Family member modals
  const [activeModal, setActiveModal] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  // Subscriber documents
  const [documents, setDocuments] = useState(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(false);
  // Wallet activation — both only feed the hidden WalletActivationCard
  // render block below; kept live for restoration.
  const [walletActivation, setWalletActivation] = useState(null); // eslint-disable-line no-unused-vars
  const [walletActivationLoading, setWalletActivationLoading] = useState(false); // eslint-disable-line no-unused-vars
  // Ayushpay Credit summary (activation status + snapshot tiles)
  const [creditSummary, setCreditSummary] = useState(null);
  const [creditSummaryLoading, setCreditSummaryLoading] = useState(false);
  // Credit line account + per-lender status snapshot (status, description, limits)
  const [creditLineDetail, setCreditLineDetail] = useState(null);
  const [creditLineDetailLoading, setCreditLineDetailLoading] = useState(false);

  // Show notification helper
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  }, []);
  
  // Custom hooks
  const {
    profile,
    wallet,
    bank,
    family,
    loading,
    error,
    setFamily
  } = useSubscriberData(id, currentRole);
  
  const {
    transactions,
    txnPagination,
    isLoading: isTxnLoading
  } = useTransactionHistory(id, currentRole, txnCategory, txnPage, txnSearch);
  
  const {
    transactionDetail,
    selectedTransactionId,
    detailLoading,
    detailError,
    loadTransactionDetail,
    closeModal: closeTransactionModal
  } = useTransactionDetail(id, currentRole);
  
  const {
    redemptions,
    redemptionPagination
  } = useRedemptionHistory(id, currentRole, redemptionPage, redemptionSearch, redemptionInitialLoadComplete, redemptionStatus);
  
  const {
    addMember,
    updateMember,
    deactivateMember
  } = useFamilyMembers(id, currentRole, family, setFamily, showNotification);
  
  // The module-detail popups, opened IN PLACE on this page.
  //
  // A medicine or diagnostics transaction belongs to its own module — those views carry the
  // delivery address / appointment, attachments, member tests and (for medicine, while
  // Initiated/Saved) an editable ops cart that the generic wallet detail modal cannot show.
  // So the subscriber journey renders the module's OWN popup component here rather than
  // navigating to the module page: closing it leaves the user exactly where they were, on
  // this subscriber's profile. The module pages' own journeys are untouched.

  const handleModuleDetailUnavailable = useCallback((_kind, transactionId) => {
    loadTransactionDetail(transactionId);
  }, [loadTransactionDetail]);

  const {
    kind: moduleKind,
    detail: moduleDetail,
    loading: moduleLoading,
    error: moduleError,
    open: openModuleDetail,
    retry: retryModuleDetail,
    close: closeModuleDetail,
  } = useModuleBookingDetail(currentRole, handleModuleDetailUnavailable);

  // Initiated/Saved medicine orders are an editable ops cart, not a read-only detail — the
  // medicine module opens `MedicineTransaction` for them, and only for Ops/SuperAdmin.
  // Same rule here, same component, same wrapper.
  const [medicineCartModal, setMedicineCartModal] = useState(null);
  const [medicineCartKey, setMedicineCartKey] = useState(0);

  const closeMedicineCartModal = useCallback(() => setMedicineCartModal(null), []);

  const handleTransactionClick = useCallback((txn) => {
    const transactionId = txn?.booking_id || txn?.transaction_id;
    if (!transactionId) return;

    setPartnerDataExpanded(false);

    const serviceType = String(txn?.service_type || txn?.description || '').trim().toLowerCase();
    const status = String(txn?.status || '').trim().toLowerCase();
    const isOps = currentRole === 'Ops' || currentRole === 'SuperAdmin';

    if (serviceType === 'medicine') {
      // Mirrors MedicineBookingProfile.handleRowClick: Initiated/Saved is the editable cart,
      // and Finance/Support get no popup at all for it.
      if (status === 'initiated' || status === 'saved') {
        if (!isOps) return;
        const applicationId = txn?.application_id || id;
        // Without an application id MedicineTransaction renders its own full-page error
        // branch — which navigates away, ignoring isModal. Never let that happen inside a
        // popup on this page; fall back to the generic wallet detail instead.
        if (!applicationId) {
          loadTransactionDetail(transactionId);
          return;
        }
        setMedicineCartModal({ applicationId, walletTransactionId: transactionId });
        setMedicineCartKey((prev) => prev + 1);
        return;
      }
      openModuleDetail(MODULE_KIND_MEDICINE, transactionId);
      return;
    }

    // Finance has no diagnostic-subscribers routes at all (finance/v4.py registers none), so
    // it keeps the generic wallet modal for diagnostics rows.
    if (serviceType === 'diagnostics' && DIAGNOSTIC_MODULE_ROLES.includes(currentRole)) {
      openModuleDetail(MODULE_KIND_DIAGNOSTICS, transactionId);
      return;
    }

    loadTransactionDetail(transactionId);
  }, [currentRole, id, loadTransactionDetail, openModuleDetail]);

  // Closing a module popup also collapses its Partner Data section, matching what the module
  // pages' own `closeModal` does.
  const handleCloseModuleDetail = useCallback(() => {
    closeModuleDetail();
    setPartnerDataExpanded(false);
  }, [closeModuleDetail]);

  // Keep this page's own URL on the tab/page being viewed, so the browser's Back button
  // from a module page restores the same list the in-page back button does, and a refresh
  // or a shared link opens it. Replace, never push, so tab clicks don't pile up history.
  useEffect(() => {
    const next = writeTransactionListState(searchParams, { txnCategory, txnPage });
    if (next.toString() !== searchParams.toString()) {
      // Keep router state so Back to List still knows which list page we came from.
      setSearchParams(next, { replace: true, state: location.state });
    }
  }, [txnCategory, txnPage, searchParams, setSearchParams, location.state]);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!id || !currentRole) return;
    // Documents (KYC scans) and wallet activation stay Ops/SuperAdmin-only —
    // do NOT widen this gate to Finance/Support (out of scope; see
    // credit-snapshot gate below for the intentionally wider one).
    if (currentRole === 'Ops' || currentRole === 'SuperAdmin') {
      setDocumentsLoading(true);
      getSubscriberDocuments(id, currentRole)
        .then(res => { if (res?.msg) setDocuments(res.msg); })
        .catch(() => { setDocumentsError(true); setDocuments([]); })
        .finally(() => setDocumentsLoading(false));
      setWalletActivationLoading(true);
      getWalletActivationDetail(id, currentRole)
        .then(res => { if (res?.msg) setWalletActivation(res.msg); })
        .catch(() => {})
        .finally(() => setWalletActivationLoading(false));
    }
    // Credit snapshot (ledger totals + credit line status) is visible to
    // all 4 admin roles — backend scopes Finance/Support to a lean payload.
    if (currentRole === 'Ops' || currentRole === 'SuperAdmin' || currentRole === 'Finance' || currentRole === 'Support') {
      setCreditSummaryLoading(true);
      getSubscriberCreditSummary(id, currentRole)
        .then(res => { if (res?.msg) setCreditSummary(res.msg); })
        .catch(() => {})
        .finally(() => setCreditSummaryLoading(false));
      setCreditLineDetailLoading(true);
      getCreditLineSubscriberDetail(currentRole, id)
        .then(res => { if (res?.msg) setCreditLineDetail(res.msg); })
        .catch(() => {})
        .finally(() => setCreditLineDetailLoading(false));
    }
  }, [id, currentRole]);

  // Role-based permissions
  const isFinance = currentRole === 'Finance';
  const isSupport = currentRole === 'Support';
  const isSuperAdmin = currentRole === 'SuperAdmin';
  const isOpsOrSuperAdmin = currentRole === 'Ops' || currentRole === 'SuperAdmin';
  // Credit snapshot card is visible to all 4 admin roles (Ops, SuperAdmin,
  // Finance, Support) — kept distinct from isOpsOrSuperAdmin, which still
  // gates documents/wallet-activation and other Ops/SuperAdmin-only UI.
  const canViewCreditSnapshot = isOpsOrSuperAdmin || isFinance || isSupport;
  
  // Singleton relations
  const SINGLETON_RELATIONS_SET = useMemo(() => new Set([
    'self', 'father', 'mother', 'spouse', 'mother_in_law', 'father_in_law'
  ]), []);
  
  // Existing singleton relations
  const existingSingletonRelations = useMemo(() => {
    const existing = new Set();
    try {
      family.forEach((member) => {
        const memberStatus = (member.status || '').toString().toLowerCase();
        const isActive = memberStatus === 'active';
        if (isActive) {
          const rel = normalizeRelationshipForApi(member.relationship || member.relation);
          if (SINGLETON_RELATIONS_SET.has(rel)) existing.add(rel);
        }
      });
    } catch {}
    return existing;
  }, [family, SINGLETON_RELATIONS_SET]);
  
  // Sort family members: "Self" always appears first
  const sortedFamily = useMemo(() => {
    const sorted = [...family];
    sorted.sort((a, b) => {
      const aRel = normalizeRelationshipForApi(a.relationship || a.relation || '');
      const bRel = normalizeRelationshipForApi(b.relationship || b.relation || '');
      const aIsSelf = aRel === 'self';
      const bIsSelf = bRel === 'self';
      if (aIsSelf && !bIsSelf) return -1;
      if (!aIsSelf && bIsSelf) return 1;
      return 0; // Keep original order for non-Self members
    });
    return sorted;
  }, [family]);
  
  // Handle redemption search
  const handleRedemptionSearchSubmit = useCallback(() => {
    const newSearch = redemptionSearchInput.trim();
    if (newSearch !== redemptionSearch) {
      setRedemptionSearch(newSearch);
      setRedemptionPage(1);
    }
    if (redemptionSearchTimeoutRef.current) {
      clearTimeout(redemptionSearchTimeoutRef.current);
      redemptionSearchTimeoutRef.current = null;
    }
  }, [redemptionSearchInput, redemptionSearch]);

  useEffect(() => {
    if (redemptionSearchTimeoutRef.current) {
      clearTimeout(redemptionSearchTimeoutRef.current);
    }
    redemptionSearchTimeoutRef.current = setTimeout(() => {
      handleRedemptionSearchSubmit();
    }, 2000);
    return () => {
      if (redemptionSearchTimeoutRef.current) {
        clearTimeout(redemptionSearchTimeoutRef.current);
      }
    };
  }, [redemptionSearchInput, handleRedemptionSearchSubmit]);
  
  // Set redemption initial load complete after data loads
  useEffect(() => {
    if (!loading && !redemptionInitialLoadComplete) {
      setRedemptionInitialLoadComplete(true);
    }
  }, [loading, redemptionInitialLoadComplete]);
  
  // Handle family member modals
  const openModal = useCallback((type, member) => {
    setSelectedMember(member);
    setActiveModal(type);
  }, []);
  
  const closeModal = useCallback(() => {
    setActiveModal(null);
    setSelectedMember(null);
  }, []);
  
  // Handle add family member
  const handleAddFamilyMember = useCallback(async (memberData) => {
    const success = await addMember(memberData);
    if (success) {
      closeModal();
    }
  }, [addMember, closeModal]);
  
  // Handle edit family member
  const handleEditFamilyMember = useCallback(async (memberId, memberData) => {
    const success = await updateMember(memberId, memberData);
    if (success) {
      closeModal();
    }
  }, [updateMember, closeModal]);
  
  // Handle deactivate family member
  const handleDeactivateFamilyMember = useCallback(async (memberId, reasonCode) => {
    const success = await deactivateMember(memberId, reasonCode);
    if (success) {
      closeModal();
    }
  }, [deactivateMember, closeModal]);
  
  // Access denied check
  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  const redemptionTotalPages = Math.max(1, Math.ceil(redemptionPagination.total / redemptionPagination.items));
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* Back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => navigate('/subscriber-wallet')} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to Home
            </button>
            <button 
              onClick={() => navigate(backToList)} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to List
            </button>
          </div>
        </div>
        
        {/* Subscriber Info Card */}
        <SubscriberInfoCard
          profile={profile}
          showPhone={showPhone}
          setShowPhone={setShowPhone}
          isFinance={isFinance}
          isSupport={isSupport}
          isSuperAdmin={isSuperAdmin}
          applicationId={id}
          currentRole={currentRole}
          onNotification={showNotification}
          creditSummary={creditSummary}
        />
        
        {/* Ayushpay Credit Snapshot — shown above Wallet Snapshot, visible to
            all 4 admin roles (Ops, SuperAdmin, Finance, Support); backend
            scopes Finance/Support to a lean payload (no member/address/
            employment/ledger data). Also carries Credit Line Status (account
            + per-lender status/description) merged into this card per
            product decision. */}
        <CreditSnapshotCard
          canView={canViewCreditSnapshot}
          creditSummary={creditSummary}
          loading={creditSummaryLoading}
          creditLineDetail={creditLineDetail}
          creditLineDetailLoading={creditLineDetailLoading}
        />

        {/* Wallet Snapshot */}
        <WalletSnapshotCard wallet={wallet} />

        {/* Ayushpay Credit Activation — hidden from the UI per product decision.
            Not deleted, only unmounted here. To restore, uncomment this block.
        <CreditActivationCard
          isOpsOrSuperAdmin={isOpsOrSuperAdmin}
          creditSummary={creditSummary}
          loading={creditSummaryLoading}
        />
        */}

        {/* Wallet Activation — hidden from the UI per product decision. The
            Regenerate PIN tool this card provides is unrelated to credit-line
            activation; it is not deleted, only unmounted here. To restore,
            uncomment this block (WalletActivationCard's import above is kept
            live for exactly this).
        {isOpsOrSuperAdmin && walletActivation?.wallet_activation_required && !walletActivation?.wallet_activated && (
          <WalletActivationCard
            applicationId={id}
            currentRole={currentRole}
            walletActivation={walletActivation}
            loading={walletActivationLoading}
            onPinGenerated={(msg) => showNotification(msg, 'success')}
            mounted={mounted}
          />
        )}
        */}

        {/* Transaction History */}
        <TransactionHistoryTable
          transactions={transactions}
          txnSearch={txnSearch}
          setTxnSearch={setTxnSearch}
          txnCategory={txnCategory}
          setTxnCategory={setTxnCategory}
          txnPage={txnPage}
          txnPagination={txnPagination}
          setTxnPage={setTxnPage}
          currentRole={currentRole}
          mobilesUnmasked={mobilesUnmasked}
          setMobilesUnmasked={setMobilesUnmasked}
          isOpsOrSuperAdmin={isOpsOrSuperAdmin}
          onTransactionClick={handleTransactionClick}
          isLoading={isTxnLoading}
        />
        
        {/* Redemption History */}
        <RedemptionHistoryTable
          redemptions={redemptions}
          redemptionSearchInput={redemptionSearchInput}
          setRedemptionSearchInput={setRedemptionSearchInput}
          handleRedemptionSearchSubmit={handleRedemptionSearchSubmit}
          redemptionStatus={redemptionStatus}
          setRedemptionStatus={(val) => { setRedemptionStatus(val); setRedemptionPage(1); }}
          redemptionPage={redemptionPage}
          redemptionTotalPages={redemptionTotalPages}
          redemptionPagination={redemptionPagination}
          setRedemptionPage={setRedemptionPage}
          currentRole={currentRole}
          approvingRequests={new Set()}
          approvedRequestIds={new Set()}
          handleApproveRequest={async () => {}}
        />
        
        {/* Bank Details */}
        <BankDetailsCard
          bank={bank}
          showAccount={showAccount}
          setShowAccount={setShowAccount}
          isSupport={isSupport}
        />
        
        {/* Documents */}
        {isOpsOrSuperAdmin && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h2 className="text-base font-semibold text-gray-900">Documents</h2>
            </div>

            {documentsLoading ? (
              <div className="px-6 py-8 flex items-center justify-center gap-2 text-sm text-gray-400">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Loading documents...
              </div>
            ) : documentsError ? (
              <div className="px-6 py-8 flex flex-col items-center gap-2 text-center">
                <svg className="w-8 h-8 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <p className="text-sm text-red-500 font-medium">Failed to load documents</p>
                <p className="text-xs text-gray-400">Please try refreshing the page</p>
              </div>
            ) : !documents ? (
              <div className="px-6 py-8 flex flex-col items-center gap-2 text-center">
                <svg className="w-8 h-8 text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm text-gray-400">No documents found</p>
              </div>
            ) : (
              <div className="px-6 py-5 space-y-6">
                {/* Policy Documents */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded bg-indigo-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Policy Documents</p>
                  </div>
                  {documents.policy_documents && documents.policy_documents.length > 0 ? (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {documents.policy_documents.map((doc) => {
                        const name = (doc.label?.name || '').toLowerCase();
                        const isInvoice = name.includes('invoice');
                        const colorSet = isInvoice
                          ? { bg: 'bg-amber-50', border: 'border-amber-100', hover: 'hover:bg-amber-50 hover:border-amber-300', iconBg: 'bg-amber-100', icon: 'text-amber-600', text: 'group-hover:text-amber-700', link: 'group-hover:text-amber-500' }
                          : { bg: 'bg-indigo-50', border: 'border-indigo-100', hover: 'hover:bg-indigo-50 hover:border-indigo-300', iconBg: 'bg-indigo-100', icon: 'text-indigo-600', text: 'group-hover:text-indigo-700', link: 'group-hover:text-indigo-500' };
                        return (
                          <a
                            key={doc.id}
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${colorSet.bg} ${colorSet.border} ${colorSet.hover} transition-all group shadow-sm`}
                          >
                            <div className={`w-10 h-10 rounded-lg ${colorSet.iconBg} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                              {isInvoice ? (
                                <svg className={`w-5 h-5 ${colorSet.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h6" />
                                </svg>
                              ) : (
                                <svg className={`w-5 h-5 ${colorSet.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold text-gray-800 truncate ${colorSet.text}`}>
                                {doc.label?.display_name || doc.label?.name || 'Document'}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {new Date(doc.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                            <div className={`w-7 h-7 rounded-full bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 shadow-sm ${colorSet.link}`}>
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No policy documents.</p>
                  )}
                </div>

                {/* Invoice Documents */}
                {/* <div>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded bg-amber-100 flex items-center justify-center">
                      <svg className="w-3 h-3 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7.414A2 2 0 0015.414 6L12 2.586A2 2 0 0010.586 2H6zm2 10a1 1 0 10-2 0v3a1 1 0 102 0v-3zm2-3a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1zm4-1a1 1 0 10-2 0v6a1 1 0 102 0V8z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Invoice Documents</p>
                  </div>
                  {documents.invoice_documents && documents.invoice_documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.invoice_documents.map(invoice => (
                        <div key={invoice.id} className="rounded-xl border border-amber-100 overflow-hidden shadow-sm">
                          <div className="flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-100">
                            <div className="w-6 h-6 rounded-md bg-amber-200 flex items-center justify-center flex-shrink-0">
                              <svg className="w-3.5 h-3.5 text-amber-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                            </div>
                            <span className="text-xs font-bold text-amber-800 tracking-wide">#{invoice.invoice_no}</span>
                            <span className="ml-auto text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">
                              {new Date(invoice.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                          {invoice.documents && invoice.documents.length > 0 ? (
                            <div className="divide-y divide-amber-50 bg-white">
                              {invoice.documents.map(doc => (
                                <a
                                  key={doc.id}
                                  href={doc.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 px-4 py-3 hover:bg-amber-50 transition-colors group"
                                >
                                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                                    <svg className="w-4 h-4 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                  </div>
                                  <span className="text-sm font-medium text-gray-700 group-hover:text-amber-700 flex-1">
                                    {doc.label?.display_name || doc.label?.name || 'Document'}
                                  </span>
                                  <div className="w-7 h-7 rounded-full bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-200 group-hover:border-amber-300 transition-colors">
                                    <svg className="w-3.5 h-3.5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                  </div>
                                </a>
                              ))}
                            </div>
                          ) : (
                            <p className="px-4 py-3 text-sm text-gray-400">No documents for this invoice.</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No invoice documents.</p>
                  )}
                </div> */}
              </div>
            )}
          </div>
        )}

        {/* Family Members */}
        <div className="bg-white border border-gray-200 rounded-md shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
            <h2 className="text-lg font-semibold text-gray-900">Family Members</h2>
                <div className="text-xs text-gray-500 mt-1">{sortedFamily.length} active members linked to subscription</div>
              </div>
            {!isFinance && !isSupport && (
              <button
                onClick={() => openModal('add_member', null)}
                className="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <FaPlus className="w-4 h-4" />
                <span>Add Member</span>
              </button>
            )}
            </div>
          </div>
          <FamilyMembersList
            family={sortedFamily}
            isFinance={isFinance}
            isSupport={isSupport}
            onEdit={(member) => openModal('edit', member)}
            onDeactivate={(member) => openModal('deactivate', member)}
            existingSingletonRelations={existingSingletonRelations}
          />
        </div>
        
        {/* Generic wallet Transaction Detail Modal — for every service type that has no
            module view of its own. Its visibility is DERIVED from the module popups so two
            detail modals can never stack. */}
        <TransactionDetailModal
          mounted={mounted}
          show={!!selectedTransactionId && !moduleKind && !medicineCartModal}
          transactionDetail={transactionDetail}
          loading={detailLoading}
          error={detailError}
          partnerDataExpanded={partnerDataExpanded}
          setPartnerDataExpanded={setPartnerDataExpanded}
          currentRole={currentRole}
          mobilesUnmasked={mobilesUnmasked}
          onClose={closeTransactionModal}
          onRetry={() => loadTransactionDetail(selectedTransactionId)}
        />

        {/* Medicine module's own booking detail — the exact component MedicineBookingProfile
            renders, opened here so the user never leaves this subscriber's profile.
            showFullMobile stays false: the module has always masked this number, and this
            page's own unmask toggle must not widen that. */}
        <MedicineBookingDetailModal
          mounted={mounted && moduleKind === MODULE_KIND_MEDICINE}
          bookingDetail={moduleDetail}
          loading={moduleLoading}
          error={moduleError}
          onRetry={retryModuleDetail}
          onClose={handleCloseModuleDetail}
          showFullMobile={false}
          partnerDataExpanded={partnerDataExpanded}
          onTogglePartnerData={() => setPartnerDataExpanded((prev) => !prev)}
        />

        {/* Diagnostic module's own booking detail — same arrangement. */}
        <DiagnosticBookingDetailModal
          mounted={mounted && moduleKind === MODULE_KIND_DIAGNOSTICS}
          bookingDetail={moduleDetail}
          loading={moduleLoading}
          error={moduleError}
          onRetry={retryModuleDetail}
          onClose={handleCloseModuleDetail}
          showFullMobile={false}
          partnerDataExpanded={partnerDataExpanded}
          onTogglePartnerData={() => setPartnerDataExpanded((prev) => !prev)}
          currentRole={currentRole}
          canSendPaymentLink={isOpsOrSuperAdmin}
        />

        {/* Editable ops cart for Initiated/Saved medicine orders (Ops/SuperAdmin only).
            Wrapper copied from MedicineBookingProfile verbatim, including the deliberate
            absence of a backdrop-click close: a stray click must not discard a half-built cart.
            No post-close refetch either: MedicineTransaction already reloads the page after
            every successful mutation in isModal mode, and this list's tab/page live in the
            URL, so the reload lands back on the same view. */}
        {mounted && medicineCartModal && createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto relative">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-bold text-gray-900">Manual Transaction - Medicine</h2>
                <button
                  onClick={closeMedicineCartModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FaTimes className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <MedicineTransaction
                  key={`medicine-transaction-${medicineCartModal.applicationId}-${medicineCartKey}`}
                  applicationId={medicineCartModal.applicationId}
                  walletTransactionId={medicineCartModal.walletTransactionId}
                  isModal={true}
                  onClose={closeMedicineCartModal}
                />
              </div>
            </div>
          </div>,
          document.body
        )}
        
        {/* Family Member Modals */}
        <AddFamilyMemberModal
          mounted={mounted}
          show={activeModal === 'add_member'}
          onClose={closeModal}
          onSubmit={handleAddFamilyMember}
          existingSingletonRelations={existingSingletonRelations}
          existingMembers={family}
        />

        <EditFamilyMemberModal
          mounted={mounted}
          show={activeModal === 'edit'}
          member={selectedMember}
          onClose={closeModal}
          onSubmit={handleEditFamilyMember}
          existingSingletonRelations={existingSingletonRelations}
          existingMembers={family}
        />
        
        <DeactivateFamilyMemberModal
          mounted={mounted}
          show={activeModal === 'deactivate'}
          member={selectedMember}
          onClose={closeModal}
          onSubmit={handleDeactivateFamilyMember}
        />
        
        {/* Notification Toast */}
        <NotificationToast
          notification={notification}
          onClose={() => setNotification(null)}
          mounted={mounted}
        />
      </div>
    </div>
  );
};

export default SubscriberProfile;
